import { Column } from 'primereact/column'
import { ColumnGroup } from 'primereact/columngroup'
import { DataTable } from 'primereact/datatable'
import { Row } from 'primereact/row'
import { useMemo, useState } from 'react'

import { Flex, Input, Modal, Text } from '../../../Components'
import { useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { formatMs } from './utils'

const language = cap.language.state || 'IT'
const t = translations[language]

const VARIANCE_HIGHLIGHT_THRESHOLD = 2

const isHighlightedRow = (row) => (row?.varianceRatio || 0) >= VARIANCE_HIGHLIGHT_THRESHOLD

const sortNumberOrNull = (a, b) => {
  const av = a == null ? Number.NEGATIVE_INFINITY : a
  const bv = b == null ? Number.NEGATIVE_INFINITY : b
  return av - bv
}

/**
 * Ordinamento custom per delta (ms) su DataTable PrimeReact.
 * @param {(row: Object) => number | null} getDelta - Calcola delta (periodo - baseline) in ms
 * @returns {(event: { data: Array, order: number }) => Array} Funzione di sort che deve **restituire** l'array ordinato
 */
const sortByDeltaMs = (getDelta) => (event) => {
  const order = event?.order ?? 1
  const data = Array.isArray(event?.data) ? [...event.data] : []
  return data.sort((r1, r2) => {
    const d1 = getDelta?.(r1)
    const d2 = getDelta?.(r2)
    return order * sortNumberOrNull(d1, d2)
  })
}

const TimeCell = ({ value, highlight }) => {
  if (value == null) return <Text value="-" size={12} style={{ color: 'var(--ink-400)' }} />

  return (
    <Text
      value={formatMs(value)}
      size={13}
      style={highlight ? { color: 'var(--danger)', fontWeight: 'bold' } : {}}
    />
  )
}

const DeltaMsCell = ({ delta }) => {
  if (delta == null) return <Text value="-" size={12} style={{ color: 'var(--ink-400)' }} />
  if (delta === 0) return <Text value="0ms" size={12} style={{ color: 'var(--ink-400)' }} />

  const color = delta > 0 ? 'var(--danger)' : 'var(--success)'
  const prefix = delta > 0 ? '+' : '-'
  return (
    <Text
      value={`${prefix}${formatMs(Math.abs(delta))}`}
      size={12}
      style={{ color, fontWeight: 'bold' }}
    />
  )
}

const ErrorRateCell = ({ value }) => {
  if (value === 0) return <span className="nbadge nbadge--ok">0%</span>
  if (value < 1) return <span className="nbadge">{value}%</span>
  if (value <= 5) return <span className="nbadge nbadge--warn">{value}%</span>
  return <span className="nbadge nbadge--err">{value}%</span>
}

/** Rapporto P90/Media nel periodo (coda vs centro). */
const VarianceRatioCell = ({ value }) => {
  if (value == null) return <Text value="-" size={12} style={{ color: 'var(--ink-400)' }} />
  const color = value >= 3 ? 'var(--danger)' : value >= 2 ? 'var(--warning)' : 'var(--ink-400)'
  return (
    <Text
      value={`×${value}`}
      size={12}
      style={{ color, fontWeight: value >= 2 ? 'bold' : undefined }}
    />
  )
}

export const UnitStatsTable = ({ startDate, endDate, onSelectUnit }) => {
  const [filter, setFilter] = useState('')
  const [highlightFilter, setHighlightFilter] = useState('all')
  const [isLegendOpen, setIsLegendOpen] = useState(false)
  const { data: stats = [], isSuccess } = useCQuery(['unitPerformanceStats', startDate, endDate])

  const filteredStats = useMemo(() => {
    let out = stats

    if (filter) {
      const lowerFilter = filter.toLowerCase()
      out = out.filter((s) => s.unit.toLowerCase().includes(lowerFilter))
    }

    if (highlightFilter !== 'all') {
      out = out.filter((row) => {
        const highlighted = isHighlightedRow(row)
        return highlightFilter === 'highlighted' ? highlighted : !highlighted
      })
    }

    return out
  }, [stats, filter, highlightFilter])

  const headerGroup = useMemo(
    () => (
      <ColumnGroup type="header">
        <Row>
          <Column header={t.colAzioni} rowSpan={2} style={{ width: 170 }} />
          <Column header={t.unit} rowSpan={2} sortable sortField="unit" style={{ minWidth: 200 }} />
          <Column header={t.colN} rowSpan={2} sortable sortField="count" style={{ width: 70 }} />
          <Column
            header={t.errori}
            rowSpan={2}
            sortable
            sortField="errorCount"
            style={{ width: 72 }}
          />
          <Column
            header={t.colErrPerc}
            rowSpan={2}
            sortable
            sortField="errorRate"
            style={{ width: 70 }}
          />
          <Column header={t.nelPeriodo} colSpan={7} />
          <Column header={t.deltaVsMediaComplessiva} colSpan={5} />
        </Row>
        <Row>
          <Column header={t.media} sortable sortField="avg" />
          <Column header="P50" sortable sortField="p50" />
          <Column header="P90" sortable sortField="p90" />
          <Column header="P95" sortable sortField="p95" />
          <Column header="P99" sortable sortField="p99" />
          <Column header={t.max} sortable sortField="max" />
          <Column header={t.colP90Media} sortable sortField="varianceRatio" />
          <Column header={t.colDeltaMedia} sortable sortField="deltaAvgMs" />
          <Column header={t.colDeltaP50} sortable sortField="deltaP50Ms" />
          <Column header={t.colDeltaP90} sortable sortField="deltaP90Ms" />
          <Column header={t.colDeltaP95} sortable sortField="deltaP95Ms" />
          <Column header={t.colDeltaP99} sortable sortField="deltaP99Ms" />
        </Row>
      </ColumnGroup>
    ),
    []
  )

  if (!isSuccess) return null

  const rowClassName = (row) => {
    if (row.errorRate > 10) return 'row-error'
    if (isHighlightedRow(row)) return 'row-warning'
    return ''
  }

  return (
    <div className="ncard perf-table-card" style={{ padding: 0, marginBottom: 18 }}>
      <div className="ncard__head" style={{ borderBottom: '1px solid var(--border)' }}>
        <div className="ncard__ico">
          <i className="pi pi-box" style={{ fontSize: 16 }} />
        </div>
        <div>
          <h3 className="ncard__title">{t.statistichePerUnit}</h3>
          <div className="ncard__sub">
            {filteredStats.length} {t.unitNelPeriodo}
          </div>
        </div>
        <div className="ncard__head-actions">
          <button type="button" className="nbtn nbtn--sm" onClick={() => setIsLegendOpen(true)}>
            <i className="pi pi-info-circle" style={{ fontSize: 12 }} />
            {t.legenda}
          </button>
        </div>
      </div>
      <div style={{ padding: '14px 18px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div className="nfield" style={{ flex: '1 1 220px' }}>
          <label>{t.cercaUnit}</label>
          <Input
            value={filter}
            id="unitFilter"
            style={{ width: '100%' }}
            onChange={({ unitFilter }) => setFilter(unitFilter)}
          />
        </div>
        <div className="nfield" style={{ flex: '1 1 220px' }}>
          <label>{t.evidenziazioneRighe}</label>
          <Input
            id="highlightFilter"
            dropdown
            value={highlightFilter}
            options={[
              { label: t.tutte, value: 'all' },
              { label: t.soloEvidenziate, value: 'highlighted' },
              { label: t.soloNonEvidenziate, value: 'not_highlighted' }
            ]}
            style={{ width: '100%' }}
            onChange={({ highlightFilter: val }) => setHighlightFilter(val)}
          />
        </div>
      </div>

      <style>{`
        .perf-table-card .perf-table .p-datatable-thead > tr > th {
          background: var(--surface-2);
          font-weight: 600;
          font-size: 11px;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--ink-500);
          padding: 12px 16px;
          border-bottom: 1px solid var(--border);
        }
        .perf-table-card .perf-table .p-datatable-tbody > tr > td {
          padding: 12px 16px;
          border-bottom: 1px solid var(--border);
          font-size: 13px;
        }
        .perf-table-card .perf-table .p-datatable-tbody > tr:hover > td {
          background: var(--surface-2);
        }
        .perf-table-card .perf-table .row-error > td { background: var(--danger-tint) !important; }
        .perf-table-card .perf-table .row-warning > td { background: var(--warning-tint) !important; }
        [data-theme='dark'] .perf-table-card .perf-table .row-error > td { background: rgba(217,45,45,0.14) !important; }
        [data-theme='dark'] .perf-table-card .perf-table .row-warning > td { background: rgba(217,119,6,0.14) !important; }
        .perf-table-card .p-paginator {
          background: var(--surface-2);
          border-top: 1px solid var(--border);
          padding: 12px 16px;
        }
      `}</style>

      <DataTable
        value={filteredStats}
        className="perf-table ntable"
        headerColumnGroup={headerGroup}
        rowClassName={rowClassName}
        sortMode="single"
        removableSort
        emptyMessage={t.nessunDatoDisponibile}
        scrollable
        scrollHeight="500px"
        size="small"
      >
        <Column
          style={{ width: 170 }}
          body={(row) => (
            <div style={{ display: 'inline-flex', gap: 4 }}>
              <button
                type="button"
                className="naction-btn naction-btn--primary"
                title={t.aggiungiAlGrafico}
                onClick={() => onSelectUnit?.(row.unit, 'chart')}
              >
                <i className="pi pi-chart-line" style={{ fontSize: 14 }} />
              </button>
              <button
                type="button"
                className="naction-btn"
                title={t.drillDown}
                onClick={() => onSelectUnit?.(row.unit, 'details')}
              >
                <i className="pi pi-eye" style={{ fontSize: 14 }} />
              </button>
            </div>
          )}
        />
        <Column
          field="unit"
          sortable
          style={{ minWidth: 200 }}
          body={(row) => (
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 500 }}>
              {row.unit}
            </span>
          )}
        />
        <Column field="count" sortable style={{ width: 70 }} />
        <Column
          field="errorCount"
          sortable
          style={{ width: 72 }}
          body={(row) => (
            <Text
              value={row.errorCount}
              size={13}
              style={row.errorCount > 0 ? { color: 'var(--danger)', fontWeight: 'bold' } : {}}
            />
          )}
        />
        <Column
          field="errorRate"
          sortable
          style={{ width: 70 }}
          body={(row) => <ErrorRateCell value={row.errorRate} />}
        />
        <Column
          field="avg"
          sortable
          style={{ width: 90 }}
          body={(row) => <TimeCell value={row.avg} />}
        />
        <Column
          field="p50"
          sortable
          style={{ width: 80 }}
          body={(row) => <TimeCell value={row.p50} />}
        />
        <Column
          field="p90"
          sortable
          style={{ width: 80 }}
          body={(row) => <TimeCell value={row.p90} />}
        />
        <Column
          field="p95"
          sortable
          style={{ width: 80 }}
          body={(row) => <TimeCell value={row.p95} />}
        />
        <Column
          field="p99"
          sortable
          style={{ width: 80 }}
          body={(row) => <TimeCell value={row.p99} />}
        />
        <Column
          field="max"
          sortable
          style={{ width: 90 }}
          body={(row) => <TimeCell value={row.max} />}
        />
        <Column
          field="varianceRatio"
          sortable
          style={{ width: 88 }}
          body={(row) => <VarianceRatioCell value={row.varianceRatio} />}
        />
        <Column
          field="deltaAvgMs"
          sortable
          sortFunction={sortByDeltaMs((row) =>
            row?.absolute?.avg == null ? null : row.avg - row.absolute.avg
          )}
          style={{ width: 92 }}
          body={(row) => (
            <DeltaMsCell delta={row?.absolute?.avg == null ? null : row.avg - row.absolute.avg} />
          )}
        />
        <Column
          field="deltaP50Ms"
          sortable
          sortFunction={sortByDeltaMs((row) =>
            row?.absolute?.p50 == null ? null : row.p50 - row.absolute.p50
          )}
          style={{ width: 88 }}
          body={(row) => (
            <DeltaMsCell delta={row?.absolute?.p50 == null ? null : row.p50 - row.absolute.p50} />
          )}
        />
        <Column
          field="deltaP90Ms"
          sortable
          sortFunction={sortByDeltaMs((row) =>
            row?.absolute?.p90 == null ? null : row.p90 - row.absolute.p90
          )}
          style={{ width: 88 }}
          body={(row) => (
            <DeltaMsCell delta={row?.absolute?.p90 == null ? null : row.p90 - row.absolute.p90} />
          )}
        />
        <Column
          field="deltaP95Ms"
          sortable
          sortFunction={sortByDeltaMs((row) =>
            row?.absolute?.p95 == null ? null : row.p95 - row.absolute.p95
          )}
          style={{ width: 88 }}
          body={(row) => (
            <DeltaMsCell delta={row?.absolute?.p95 == null ? null : row.p95 - row.absolute.p95} />
          )}
        />
        <Column
          field="deltaP99Ms"
          sortable
          sortFunction={sortByDeltaMs((row) =>
            row?.absolute?.p99 == null ? null : row.p99 - row.absolute.p99
          )}
          style={{ width: 88 }}
          body={(row) => (
            <DeltaMsCell delta={row?.absolute?.p99 == null ? null : row.p99 - row.absolute.p99} />
          )}
        />
      </DataTable>

      <Modal
        visible={isLegendOpen}
        onHide={() => setIsLegendOpen(false)}
        header={t.legendaColoriRegole}
        className="nora-dialog"
        contentStyle={{ padding: 0 }}
        style={{ width: 'min(620px, 92vw)' }}
      >
        <div
          className="nora-dialog__body"
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          <div className="nora-dialog__section">
            <div className="nora-dialog__section-title">{t.evidenziazioneRighe}</div>
            <Flex fw style={{ gap: 6 }}>
              <Text value={t.pfLegendaRosso} size={13} />
              <Text
                value={t.pfLegendaGiallo.replace('${threshold}', VARIANCE_HIGHLIGHT_THRESHOLD)}
                size={13}
              />
            </Flex>
          </div>
          <div className="nora-dialog__section">
            <div className="nora-dialog__section-title">{t.pfVarianzaP90Media}</div>
            <Flex fw style={{ gap: 6 }}>
              <Text value={t.pfRapportoP90Media} size={13} />
              <Text value={t.pfValoriAltiCoda} size={13} />
            </Flex>
          </div>
          <div className="nora-dialog__section">
            <div className="nora-dialog__section-title">{t.pfMediaAssolutaColonne}</div>
            <Flex fw style={{ gap: 6 }}>
              <Text value={t.pfBaselineCalcolata} size={13} />
              <Text value={t.pfColonneDeltaMostrano} size={13} />
            </Flex>
          </div>
        </div>
        <div className="nora-dialog__body nora-dialog__footer">
          <button type="button" className="nbtn" onClick={() => setIsLegendOpen(false)}>
            {t.chiudi}
          </button>
        </div>
      </Modal>
    </div>
  )
}
